import {
  PreScreeningQuestion,
} from "../../types/landingPage";
import {
  covid50to64HealthyScreeningQuestions,
  covidVaccineTrialRouting,
} from "./COVID50to64HealthyScreener";

/**
 * Variant C: Simplified answer labels for COVID-19 Vaccine Studies - Healthy Adults 50-64
 *
 * This is an A/B/C test variant that uses shorter, more succinct answer labels
 * while preserving identical screening logic, disqualification rules, and attribute mappings.
 *
 * All question IDs, option values, disqualify functions, and attributeMappings are
 * inherited from the original screener. Only the `label` text on options is changed.
 */

/** Map of question ID → option value → simplified label */
const simplifiedLabels: Record<string, Record<string, string>> = {
  hypertension: {
    no: "No",
    yes: "Yes",
  },
  mood_disorder: {
    current_depression_symptoms: "Depression with current symptoms (such as low mood, tiredness, changes in appetite, or loss of interest in activities)",
    bipolar_disorder: "Bipolar disorder",
    schizophrenia: "Schizophrenia",
    schizoaffective_disorder: "Schizoaffective disorder",
  },
  diabetes: {
    no: "I don't have diabetes",
    type1: "Type 1",
    type2: "Type 2",
  },
  cardiovascular_conditions: {
    stroke_cva: "Stroke, mini-stroke, or TIA",
    heart_attack: "Heart attack",
    heart_failure: "Heart failure",
    coronary_artery_disease: "Coronary artery disease",
    heart_inflammation_recent: "Myocarditis or pericarditis (heart inflammation) in the past 3 months",
    cardiomyopathy: "Cardiomyopathy (heart muscle disease)",
    pulmonary_hypertension: "Pulmonary hypertension (high blood pressure in the lungs)",
  },
  asthma: {
    no: "I don't have asthma",
    yes_mild: "Yes, but I don't take daily asthma medication",
    yes_moderate_severe: "Yes, and I take daily asthma medication",
  },
  chronic_lung_disease: {
    copd: "COPD or chronic bronchitis",
    interstitial_lung_disease: "Lung fibrosis or ILD",
    pulmonary_embolism: "Pulmonary embolism (blood clot in the lungs)",
  },
  immunocompromised_conditions: {
    organ_transplant: "Had an organ transplant or am on the transplant waitlist",
    immunosuppressive_medications: "Taking immunosuppressive medications (such as prednisone, methotrexate, etc.)",
    active_tb: "Active tuberculosis or currently on tuberculosis treatment",
  },
  hiv_status: {
    no: "I don't have HIV",
    controlled: "I have HIV that is well-controlled",
    uncontrolled: "I have HIV that is not well-controlled or not treated",
  },
  neurological_conditions: {
    dementia: "Dementia",
  },
  adhd_diagnosis: {
    no: "No",
    yes: "Yes",
  },
  smoking_status: {
    no: "I have never regularly smoked",
    quit_over_6_months: "I quit more than 6 months ago",
    quit_within_6_months: "I quit within the past 6 months",
    yes_regularly: "I currently smoke",
  },
  other_exclusionary_conditions: {
    cancer_recent: "Cancer within the past 5 years (excluding basal cell carcinoma, squamous cell carcinoma, and pre-cancerous lesions)",
    chronic_kidney_disease: "Chronic kidney disease",
    hepatitis: "Hepatitis B or C",
  },
  concurrent_trial: {
    no: "No",
    yes: "Yes",
  },
};

/** Map of question ID → simplified question text */
const simplifiedQuestions: Record<string, string> = {
  diabetes: "Have you been diagnosed with diabetes?",
  cardiovascular_conditions: "Have you experienced or been diagnosed with any of the following cardiovascular conditions?",
  chronic_lung_disease: "Have you been diagnosed with any of the following lung conditions?",
  adhd_diagnosis: "Have you been diagnosed by a healthcare provider with ADHD?",
  smoking_status: "Do you have a history of regularly smoking cigarettes, e-cigarettes or vapes?",
};

/** Map of question ID → simplified help text */
const simplifiedHelpText: Record<string, string> = {
  smoking_status: "\"Regularly\" means smoking or vaping on most days or as part of a routine (not occasional or social use).",
};

/** Map of question ID → desired option order (by value) */
const optionOrder: Record<string, string[]> = {
  mood_disorder: ["bipolar_disorder", "schizophrenia", "schizoaffective_disorder", "current_depression_symptoms", "none"],
  cardiovascular_conditions: ["stroke_cva", "heart_attack", "heart_failure", "coronary_artery_disease", "cardiomyopathy", "pulmonary_hypertension", "heart_inflammation_recent", "none"],
};

export const covid50to64SimplifiedScreeningQuestions: PreScreeningQuestion[] =
  covid50to64HealthyScreeningQuestions.map((question) => {
    const labelOverrides = simplifiedLabels[question.id];
    const questionOverride = simplifiedQuestions[question.id];
    const helpTextOverride = simplifiedHelpText[question.id];
    const orderOverride = optionOrder[question.id];

    // No overrides for this question (e.g., age, height, weight, recent COVID yes/no)
    if (!labelOverrides && !questionOverride && !helpTextOverride && !orderOverride) {
      return question;
    }

    // Apply label overrides to options
    let options = labelOverrides
      ? question.options?.map((option) => ({
          ...option,
          label: labelOverrides[option.value] ?? option.label,
        }))
      : question.options;

    // Reorder options if specified
    if (orderOverride && options) {
      const optionMap = new Map(options.map((opt) => [opt.value, opt]));
      options = orderOverride
        .map((value) => optionMap.get(value))
        .filter((opt): opt is NonNullable<typeof opt> => opt !== undefined);
    }

    // Override question text, help text, and/or labels on options
    return {
      ...question,
      ...(questionOverride && { question: questionOverride }),
      ...(helpTextOverride && { helpText: helpTextOverride }),
      options,
    };
  });

// Re-export trial routing for convenience (same logic for all variants)
export { covidVaccineTrialRouting };
